<template>
	<div>
		<SectionHeader :title="t('common.observation')" />

		<!-- Main action button -->
		<ion-button
			expand="block"
			color="semilight"
			@click="handleMainButtonClick"
		>
			<div class="flex w-full items-center justify-between">
				<div class="flex items-center">
					<ion-icon
						v-if="!modelValue || modelValue.length === 0"
						:icon="addOutline"
						class="mr-2"
					/>
					<ion-icon
						v-else
						:icon="checkmarkOutline"
						class="mr-2"
						color="success"
					/>
					<span>{{ smartButtonText }}</span>
				</div>
				<div class="flex items-center">
					<ion-button
						v-if="modelValue && modelValue.length > 0"
						fill="clear"
						size="small"
						@click.stop="openSelectionModal"
					>
						<ion-icon :icon="createOutline" />
					</ion-button>
					<ion-icon
						v-if="modelValue && modelValue.length > 0"
						:icon="
							showSelectedDetails
								? chevronUpOutline
								: chevronDownOutline
						"
					/>
				</div>
			</div>
		</ion-button>

		<!-- Selected observations details (when expanded) -->
		<div
			v-if="modelValue && modelValue.length > 0 && showSelectedDetails"
			class="mx-[2px] mt-2 rounded-xl px-3"
			style="background-color: var(--ion-color-semilight)"
		>
			<ObservationItem
				v-for="(selection, index) in modelValue"
				:key="`${selection.categoryId}-${selection.subcategoryId}`"
				:observation="selection"
				:show-border="index < modelValue.length - 1"
				:show-remove="true"
				@remove="
					removeSelection(
						selection.categoryId,
						selection.subcategoryId,
					)
				"
			/>
		</div>

		<!-- Selection Modal -->
		<ObservationSelector ref="observationSelector" v-model="modelValue" />
	</div>
</template>

<script setup lang="ts">
	import { ref, computed } from "vue";
	import { IonButton, IonIcon } from "@ionic/vue";
	import {
		addOutline,
		checkmarkOutline,
		chevronDownOutline,
		chevronUpOutline,
		createOutline,
	} from "ionicons/icons";
	import SectionHeader from "@/components/feature/notes/SectionHeader.vue";
	import ObservationItem from "@/components/feature/notes/ObservationItem.vue";
	import ObservationSelector from "@/components/feature/notes/ObservationSelector.vue";
	import { useI18n } from "vue-i18n";
	import { NoteObservationDTO } from "seedgreen-shared/dtos/NoteObservationDTO";

	const { t } = useI18n();

	const modelValue = defineModel<NoteObservationDTO[]>({
		default: () => [],
	});

	// State
	const showSelectedDetails = ref(false);
	const observationSelector = ref<InstanceType<typeof ObservationSelector>>();

	// Computed properties
	const smartButtonText = computed(() => {
		if (!modelValue?.value || modelValue.value.length === 0) {
			return t("note.add-observations");
		}
		const count = modelValue.value.length;
		return t("note.observation-selected", { count });
	});

	// Methods
	const handleMainButtonClick = (): void => {
		if (!modelValue?.value || modelValue.value.length === 0) {
			// No selections - open modal to add observations
			openSelectionModal();
		} else {
			// Has selections - toggle details
			showSelectedDetails.value = !showSelectedDetails.value;
		}
	};

	const openSelectionModal = (): void => {
		observationSelector.value?.openModal();
		showSelectedDetails.value = false;
	};

	const removeSelection = (
		categoryId: number,
		subcategoryId: number,
	): void => {
		if (!modelValue?.value) return;
		modelValue.value = modelValue.value.filter(
			(selection) =>
				!(
					selection.categoryId === categoryId &&
					selection.subcategoryId === subcategoryId
				),
		);

		// Auto-collapse if no selections left
		if (modelValue.value.length === 0) {
			showSelectedDetails.value = false;
		}
	};
</script>
